/* La Sura - small form details for the cake builder (loop 5, DECISIONS
   2026-09-27).

   1. Follow-up answers. A choice that asks one more question ("Which
      number?" on a number topper, from the entry's detail_label field)
      renders a text field under its group, tied to the choice by
      data-detail-for. The field shows, posts and is required only while
      that choice is picked and not filtered out by the dietary needs.
      Without this script it stays visible and optional.

   2. Empty text posts nothing. A field marked data-name-when-filled (the
      piped message) loses its name while empty, so the order carries no
      blank "Message:" line. The same way the photo slots work.

   3. Made-to-quote choices (an entry ticked "Needs an enquiry"). Picking
      one takes the add button out of use and shows the builder's Start an
      enquiry button in its place (CSS in blocks/lasura-cake-builder.liquid).
      That link carries the choices to the enquiry form
      (assets/lasura-cake-prefill.js). Moved here from the builder in loop 5.

   4. A size the new shape doesn't come in: moved to assets/lasura-cake-size.js
      (2026-10-19), with the line that says so and the shopper's own size put back.

   5. Picking a size, height or shape leaves the page where it is (Matt,
      2026-09-27). Horizon's variant update redraws the option buttons,
      whose serves and price lines come and go by variant, so the rows above
      the picked button change height and the button slid under the finger
      (Chrome's scroll anchoring did not catch it). It's put back where it
      was, at the update and for a few frames after, including the second
      update when lasura-cake-size.js moves the size. */
(() => {
  if (window.lasuraCakeDetails) return;
  window.lasuraCakeDetails = true;

  const syncDetails = () =>
    document.querySelectorAll('[data-detail-for]').forEach((box) => {
      const choice = document.getElementById(box.dataset.detailFor);
      const on = !!choice && choice.checked && !choice.disabled && !choice.closest('[hidden]');
      const field = box.querySelector('[data-detail]');
      box.hidden = !on;
      if (!field) return;
      field.disabled = !on;
      field.required = on;
    });

  const syncName = (field) => {
    if (!field.dataset.name) field.dataset.name = field.getAttribute('name') || '';
    if (field.value.trim()) field.setAttribute('name', field.dataset.name);
    else field.removeAttribute('name');
  };

  /* A photo to print (Photo print's follow-up): refused over the size limit or
     under 800 pixels on its short side, and flagged under 1500, because a small
     photo or a screenshot prints blurry. A refused photo is cleared, so the
     field's required check asks again. Formats the browser can't decode (HEIC
     outside Safari) are let through unchecked. */
  const checkPhoto = (input) => {
    const status = document.getElementById(`${input.id}-status`);
    const file = input.files && input.files[0];
    const say = (text, refuse) => {
      if (status) status.textContent = text;
      input.toggleAttribute('aria-invalid', !!refuse);
      if (refuse) input.value = '';
    };
    say('', false);
    if (!file) return;
    const mb = Number(input.dataset.maxMb || 10);
    if (file.size > mb * 1024 * 1024) return say((input.dataset.tooBig || '').replace('{mb}', mb), true);
    const img = new Image(), url = URL.createObjectURL(file);
    img.onload = () => {
      const short = Math.min(img.naturalWidth, img.naturalHeight);
      URL.revokeObjectURL(url);
      if (short < 800) say(input.dataset.tooSmall || '', true);
      else if (short < 1500) say(input.dataset.small || '', false);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  };

  const syncEnquiry = () =>
    document.querySelectorAll('lasura-cake-builder[data-form-id]').forEach((builder) => {
      /* An enquiry-only choice in the builder, or an enquiry-only shape in
         the variant picker (snippets/lasura-variant-note.liquid). */
      const picked =
        [...builder.querySelectorAll('[data-enquiry-only="true"]')].some(
          (opt) => !opt.closest('[hidden]') && opt.querySelector('input:checked')
        ) ||
        [...document.querySelectorAll('variant-picker input:checked')].some((input) =>
          input.closest('label')?.querySelector('[data-enquiry-only]')
        );
      const note = builder.querySelector('[data-builder-enquiry]');
      if (note) note.hidden = !picked;
      const form = document.getElementById(builder.dataset.formId);
      form?.querySelectorAll('[name="add"], button[type="submit"]').forEach((button) => {
        if (!picked && !button.hasAttribute('data-lasura-enquiry')) return;
        button.disabled = picked;
        button.toggleAttribute('data-lasura-enquiry', picked);
        button.setAttribute('aria-disabled', String(picked));
      });
    });

  /* The allergy question needs an answer: a need, or "Nothing to avoid"
     (blocks/_lasura-cake-dietary.liquid). */
  const syncDiet = () =>
    document.querySelectorAll('[data-builder-dietary]').forEach((group) => {
      const none = group.querySelector('[data-dietary-none]');
      if (!none) return;
      none.required = !group.querySelector('[data-dietary-need]:checked');
      if (!none.required || none.checked) none.removeAttribute('aria-invalid');
    });

  let held = null;
  const keep = (hold, frames) => {
    /* Stops once the shopper scrolls or picks again. */
    if (!hold || held !== hold) return;
    const input = [...document.querySelectorAll('variant-picker input')].find((el) => el.name === hold.name && el.value === hold.value);
    /* Gone from view: one question per step has moved on (lasura-cake-steps.js),
       so there's nothing to hold, and a hidden button measures as 0. */
    if (!input?.closest('label, fieldset')?.getClientRects().length) return (held = null);
    const shift = input.getBoundingClientRect().top - hold.top;
    if (Math.abs(shift) >= 1) {
      /* At once, not with the page's smooth scrolling. */
      const root = document.documentElement.style;
      const was = root.scrollBehavior;
      root.scrollBehavior = 'auto';
      scrollBy(0, shift);
      /* Beside a pinned preview (desktop) the page scroll may not move the
         button: undo and leave it. */
      const stuck = Math.abs(input.getBoundingClientRect().top - hold.top) >= Math.abs(shift) - 1;
      if (stuck) scrollBy(0, -shift);
      root.scrollBehavior = was;
      if (stuck) return (held = null);
    }
    if (frames > 0) requestAnimationFrame(() => keep(hold, frames - 1));
    else held = null;
  };
  ['wheel', 'touchmove', 'keydown'].forEach((type) => addEventListener(type, () => (held = null), { passive: true }));

  const init = () => {
    syncDetails();
    syncDiet();
    syncEnquiry();
    document.querySelectorAll('[data-name-when-filled]').forEach(syncName);
  };

  /* On document, so it runs after the builder's own change handler has
     applied the dietary filter to the choices. */
  document.addEventListener('change', (event) => {
    const input = event.target;
    /* A move made by a script (lasura-cake-size.js) keeps the shopper's own pick held,
       for the update it causes too. */
    if (!event.isTrusted) held = held && { ...held };
    else held = input.matches?.('variant-picker input') ? { name: input.name, value: input.value, top: input.getBoundingClientRect().top } : null;
    if (input.matches?.('[data-detail-photo]')) checkPhoto(input);
    syncDetails();
    syncDiet();
    syncEnquiry();
  });
  /* Horizon redraws the buy buttons when the size or height changes. */
  document.addEventListener('variant:update', () => {
    keep(held, 10);
    setTimeout(syncEnquiry, 0);
  });
  document.addEventListener('input', (event) => {
    if (event.target.matches && event.target.matches('[data-name-when-filled]')) syncName(event.target);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
